import { useMutation } from '@apollo/client';
import { Player } from '@lottiefiles/react-lottie-player';
import { navigate } from 'gatsby';
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';

import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';

import useMemberInfo, { IFragment, ILegalName, SubscriptionInfo } from '@hooks/useMemberInfo';
import useSubscriptionInfo from '@hooks/useSubscriptionInfo';

import { doesNotExist, exists } from '@utils/general';

import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import PageLoader from '@assets/lottie/PageLoading.json';

import { legalName, legalNameName } from '@graphql/fragments/legalName';
import { membership, membershipName } from '@graphql/fragments/membership';
import REACTIVATE_MEMBER_SUBSCRIPTION_MUTATION from '@graphql/mutations/Subscription/ReactivateMemberSubscription.mutation.gql';
import UNCANCEL_MEMBER_SUBSCRIPTION_MUTATION from '@graphql/mutations/Subscription/UncancelMemberSubscription.mutation.gql';

type ReactivateMembershipHandlerProps = {
	isPendingCancellation?: boolean;
	onSuccess?: () => void;
	onFailure?: () => void;
	currentBillingInterval?: SubscriptionInfo['membershipSubscription']['billingInterval'];
	selectedBillingInterval?: SubscriptionInfo['membershipSubscription']['billingInterval'];
};

enum StatusOptions {
	NOT_STARTED = 'NOT_STARTED',
	PROCESSING = 'PROCESSING',
	SUCCESS = 'SUCCESS',
	FAILURE = 'FAILURE',
}

export const Loading = () => (
	<CenteredGridItem>
		<Player autoplay loop src={PageLoader} />
	</CenteredGridItem>
);

export const Failure = (_?: { header?: string }) => (
	<FlexColumn gap="md">
		{exists(_?.header) && <HeadingText>{_?.header}</HeadingText>}
		<BasicText>Oops! Your payment method failed. Please update your card on file.</BasicText>
		<Button
			variant="secondary"
			onClick={() => navigate('/app/member/billing/payment_edit')}
			label="Update my card"
		/>
	</FlexColumn>
);

const ReactivateMembershipHandler: FC<ReactivateMembershipHandlerProps> = ({
	onSuccess,
	onFailure,
	isPendingCancellation = false,
	currentBillingInterval,
	selectedBillingInterval,
}) => {
	const [status, setStatus] = useState(StatusOptions.PROCESSING);

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);

	const [uncancelMemberSubscription] = useMutation(UNCANCEL_MEMBER_SUBSCRIPTION_MUTATION, {
		client: graphQLv2Client,
	});

	const [reactivateMemberSubscription] = useMutation(REACTIVATE_MEMBER_SUBSCRIPTION_MUTATION, {
		client: graphQLv2Client,
	});

	const { canceledSubscriptionInfo } = useSubscriptionInfo('MEMBERSHIP');

	const fragments: IFragment[] = useMemo(
		() => [
			{ name: membershipName, node: membership },
			{ name: legalNameName, node: legalName },
		],
		[]
	);
	const { refetchMemberInfo } = useMemberInfo<SubscriptionInfo & ILegalName>(fragments);

	useEffect(() => {
		if (!isPendingCancellation && doesNotExist(canceledSubscriptionInfo)) {
			return;
		}

		const handleReactivate = isPendingCancellation
			? uncancelMemberSubscription
			: reactivateMemberSubscription;

		handleReactivate({
			variables: {
				authenticatedMember: true,
				...(!!selectedBillingInterval && { billingInterval: selectedBillingInterval }),
			},
		})
			.then(async () => {
				await refetchMemberInfo();
				setStatus(StatusOptions.SUCCESS);
				onSuccess?.();
			})
			.catch(async (err) => {
				await refetchMemberInfo();
				console.log('err: ');
				console.log(err);
				setStatus(StatusOptions.FAILURE);
				onFailure?.();
			});
	}, [
		refetchMemberInfo,
		canceledSubscriptionInfo,
		isPendingCancellation,
		onFailure,
		onSuccess,
		reactivateMemberSubscription,
		uncancelMemberSubscription,
		selectedBillingInterval,
		currentBillingInterval,
	]);

	switch (status) {
		default:
		case StatusOptions.NOT_STARTED:
			return null;
		case StatusOptions.PROCESSING:
			return <Loading />;
		case StatusOptions.FAILURE:
			return <Failure />;
		case StatusOptions.SUCCESS:
			if (isPendingCancellation) {
				navigate('/app/member/billing', { replace: true });
			} else {
				navigate('../success', { replace: true });
				return null;
			}
	}
};

export default ReactivateMembershipHandler;
